import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { YouTubePlayerModule } from '@angular/youtube-player';
import { DateTime } from 'luxon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription, take } from 'rxjs';
import { PrimaryButtonComponent } from '../../basic/buttons/primary-button/primary-button.component';
import { ChartDataItem, ChartSeriesDataItem } from '../../basic/chart/chart-data-item';
import { LineChartComponent } from '../../basic/chart/line-chart/line-chart.component';
import { ContentModalComponent } from '../../basic/modal/content-modal/content-modal.component';
import { PhaseHeadComponent } from '../../basic/shape/phase-head/phase-head.component';
import { PhaseComponent } from '../../basic/shape/phase/phase.component';
import { BoardFuncs } from '../../core/board/board.func';
import { BoardHolderService } from '../../core/board/board.holder.service';
import { CoreItemType } from '../../core/core-item/core-item';
import { CoreItemService } from '../../core/core-item/core-item.service';
import { GoalService } from '../../core/goal/goal.service';
import { ResumeService } from '../../core/resume/resume.service';
import { SessionTemplateService } from '../../core/session-template/session.template.service';
import { SessionService } from '../../core/session/session.service';
import { Task } from '../../core/task/task';
import { TaskService } from '../../core/task/task.service';
import { UserClerkService } from '../../core/user/user-clerk.service';
import { MyAccountHeaderPortraitComponent } from '../../domain/my-account-header-portrait/my-account-header-portrait.component';
import { ProfileCardComponent } from '../../domain/profile-card/profile-card.component';
import { SessionTemplateCardComponent } from '../../domain/session-template/session-template-card/session-template-card.component';
import { ScenarioTaskCardComponent } from '../../domain/task/scenario-task-card/scenario-task-card.component';
import { AppColor } from '../../shared/color/color';
import { LambdaService } from '../../shared/core/lambda.service';
import { EnumUtils } from '../../shared/utils/enum-utils';
import { LuxonUtils } from '../../shared/utils/luxon-utils';
import { BasicPageComponent } from '../_shared/basic-page/basic-page.component';
import { OnboardingModalComponent } from './onboarding-modal/onboarding-modal.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    YouTubePlayerModule,

    NzStepsModule,
    NzButtonModule,
    NzIconModule,
    NzSkeletonModule,
    NzTagModule,

    LineChartComponent,
    BasicPageComponent,
    PrimaryButtonComponent,
    MyAccountHeaderPortraitComponent,
    OnboardingModalComponent,
    ProfileCardComponent,
    SessionTemplateCardComponent,
    PhaseHeadComponent,
    PhaseComponent,
    ContentModalComponent,
    ScenarioTaskCardComponent,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent {
  isMobile = signal<boolean>(false);

  showMobileModal = signal<boolean>(false);

  isOnboardingModalVisible = false;

  isMyActivityLoading = signal<boolean>(false);
  isMyAssignmentsLoading = signal<boolean>(false);

  sessionTemplateList = toSignal(this.sessionTemplateService.itemList$(), { initialValue: [] });

  resumeList = toSignal(this.resumeService.resumeList$(), { initialValue: undefined });
  getResume = (id?: string) => computed(() => this.resumeList()?.find((resume) => resume.id === id));

  myTaskList = signal<Task[]>([]);

  myTastListSubscription?: Subscription;

  chartDateItemList = signal<ChartSeriesDataItem[]>([]);

  constructor(
    private coreItemService: CoreItemService,
    private taskService: TaskService,
    private deviceService: DeviceDetectorService,
    private router: Router,
    private goalService: GoalService,
    private resumeService: ResumeService,
    private lambdaService: LambdaService,
    private boardHolderService: BoardHolderService,
    private sessionService: SessionService,
    private userClerkService: UserClerkService,
    private breakpointObserver: BreakpointObserver,
    private sessionTemplateService: SessionTemplateService
  ) {
    effect(() => {
      const resumeList = this.resumeList();

      if (resumeList?.length === 0) {
        this.isOnboardingModalVisible = true;
      }
    });
  }

  ngOnInit() {
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe((result) => {
      this.isMobile.set(result.matches);
    });

    this.isMyActivityLoading.set(true);

    this.coreItemService
      .getKeysOnlyByCoreItemTypeListNoCache$(
        [CoreItemType.session],
        {
          startTime: DateTime.local().minus({ month: 1 }),
          endTime: DateTime.local().plus({ days: 1 }),
        },
        this.getMyBoard()!.id
      )
      .pipe(take(1))
      .subscribe((keysItemList: { id: string; sk: string; createdById: string }[]) => {
        const createdByIdAndSessionList = new Map<string, { id: string; sk: string; createdById: string }[]>();

        for (const session of keysItemList) {
          if (session.createdById) {
            if (!createdByIdAndSessionList.has(session.createdById)) {
              createdByIdAndSessionList.set(session.createdById, []);
            }

            createdByIdAndSessionList.get(session.createdById)?.push(session);
          }
        }

        this.chartDateItemList.update((_) => {
          const result: ChartSeriesDataItem[] = [];

          const colorList = EnumUtils.getAllValues(AppColor);

          let i = 0;

          createdByIdAndSessionList.forEach((sessionList, id) => {
            const createdByList = this.coreItemService.getCoreItemListByIdList([id]);

            const createdTimeStrAndCount = new Map<string, number>();

            for (const session of sessionList) {
              const createdDateStr = session.sk.split('#')[1];
              const createdDate = DateTime.fromFormat(createdDateStr, 'yyyyMMdd');

              const day = LuxonUtils.toIsoDay(createdDate);
              if (!createdTimeStrAndCount.has(day)) {
                createdTimeStrAndCount.set(day, 0);
              }

              createdTimeStrAndCount.set(day, createdTimeStrAndCount.get(day)! + 1);
            }

            const series: ChartDataItem[] = [];

            createdTimeStrAndCount.forEach((count, createdDayStr) => {
              series.push({
                id: createdDayStr,
                name: LuxonUtils.toJSDateKeepLocalTime(DateTime.fromISO(createdDayStr))!,
                value: count,
              });
            });

            result.push({
              id,
              name: BoardFuncs.getName(createdByList[0]),
              series,
              color: colorList[i % colorList.length],
            });
            i++;
          });

          this.isMyActivityLoading.set(false);

          return result;
        });
      });

    if (this.deviceService.isMobile()) {
      this.showMobileModal.set(true);
    }

    this.isMyAssignmentsLoading.set(true);

    this.myTastListSubscription = this.taskService.getTaskList$([this.getMyBoard()!.id]).subscribe((data) => {
      this.myTaskList.set((data ?? []) as Task[]);
      this.isMyAssignmentsLoading.set(false);
    });
  }

  ngOnDestroy() {
    this.myTastListSubscription?.unsubscribe();
  }

  getMyBoard = computed(() => {
    return this.boardHolderService.myPersonalBoard();
  });

  get todayStr() {
    return DateTime.local().toFormat('cccc, d LLLL');
  }

  getUser() {
    return this.userClerkService.getUser();
  }

  getGreetings() {
    return LuxonUtils.getGreetings(DateTime.local());
  }

  isAddScenarioTaskVisible = false;

  onAddScenarioTaskClick() {
    this.isAddScenarioTaskVisible = true;
  }
}
